import React, { useState } from 'react';
import { Check, Music, Heart, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { services } from '../data/mock';
import { Link } from 'react-router-dom';

export const Services = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'Tous les services', icon: Sparkles },
    { id: 'premium', name: 'Formule Premium', icon: Heart },
    { id: 'individual', name: 'Services individuels', icon: Music }
  ];

  const filteredServices = services.filter(service => {
    if (selectedCategory === 'all') return true;
    if (selectedCategory === 'premium') return service.highlight;
    if (selectedCategory === 'individual') return !service.highlight;
    return true;
  });

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-ivory via-pearl to-light-gold/20 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-charcoal mb-6 leading-tight">
            Nos Services
          </h1>
          <p className="text-xl text-warm-gray mb-8 max-w-3xl mx-auto leading-relaxed">
            Une gamme complète de prestations musicales pour accompagner chaque moment 
            de votre mariage, du cocktail à la soirée dansante.
          </p>
          
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mt-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                data-testid={`services-filter-${category.id}`}
                className={`flex items-center space-x-2 px-6 py-3 rounded-full transition-all duration-300 font-medium border-2 ${
                  selectedCategory === category.id
                    ? 'bg-blue-900 border-blue-900 text-white shadow-lg'
                    : 'bg-white border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white'
                }`}
              >
                <category.icon className="h-4 w-4" />
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filteredServices.map((service) => (
              <Card key={service.id} className={`group hover:shadow-xl transition-all duration-300 border-0 overflow-hidden ${
                service.highlight 
                  ? 'bg-gradient-to-br from-blue-50 to-blue-100 ring-2 ring-blue-200' 
                  : 'bg-gradient-to-br from-ivory/30 to-pearl/30'
              }`}>
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-3 rounded-lg">
                        <Music className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-charcoal group-hover:text-blue-900 transition-colors">
                          {service.title}
                        </CardTitle>
                        <p className="text-sm text-gold font-medium">{service.subtitle}</p>
                      </div>
                    </div>
                    {service.highlight && (
                      <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white px-3 py-1 rounded-full text-xs font-medium">
                        Premium
                      </div>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="pt-0">
                  <p className="text-warm-gray mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="space-y-3 mb-6">
                    <h4 className="font-semibold text-charcoal">Inclus dans cette prestation :</h4>
                    <ul className="space-y-2">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-1 rounded-full mt-0.5">
                            <Check className="h-3 w-3 text-white" />
                          </div>
                          <span className="text-sm text-charcoal leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {service.highlight && (
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg mb-4">
                      <p className="text-sm text-charcoal font-medium flex items-center">
                        <Sparkles className="h-4 w-4 text-gold mr-2" />
                        Formule la plus demandée par nos clients
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-ivory/30 to-pearl/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Comment ça se passe ?
            </h2>
            <p className="text-xl text-warm-gray max-w-2xl mx-auto">
              Un processus simple et personnalisé pour votre tranquillité d'esprit
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "1",
                title: "Premier Contact",
                description: "Échange sur vos souhaits, votre vision et les détails de votre événement"
              },
              {
                step: "2", 
                title: "Devis Personnalisé",
                description: "Proposition adaptée à vos besoins avec un devis détaillé et transparent"
              },
              {
                step: "3",
                title: "Préparation",
                description: "Validation du programme musical et coordination avec vos autres prestataires"
              },
              {
                step: "4",
                title: "Jour J",
                description: "Prestation professionnelle pour créer des moments inoubliables"
              }
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-900 to-blue-700 rounded-full flex items-center justify-center mx-auto group-hover:shadow-lg transition-all duration-300">
                    <span className="text-2xl font-bold text-white">{item.step}</span>
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-blue-300 to-blue-500 -z-10"></div>
                  )}
                </div>
                <h3 className="text-lg font-bold text-charcoal mb-3 group-hover:text-blue-900 transition-colors">
                  {item.title}
                </h3>
                <p className="text-warm-gray text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à discuter de votre projet ?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Chaque événement est unique. Contactez-nous pour une consultation personnalisée 
            et recevez un devis adapté à vos besoins.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              asChild
              className="bg-white text-blue-900 hover:bg-ivory hover:text-charcoal text-lg px-8 py-4 hover:shadow-lg transition-all duration-300"
              data-testid="services-cta-devis"
            >
              <Link to="/contact">
                Demander un devis
              </Link>
            </Button>
            <Button 
              size="lg"
              variant="outline"
              asChild
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-4 transition-all duration-300"
              data-testid="services-cta-portfolio"
            >
              <Link to="/portfolio">
                Voir nos réalisations
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
